import type { PeerComparisonResponse, ScoreComparison } from '../api/rankingApi';

export type ScoreBucketKey = 'overall' | 'coding' | 'technical' | 'gd';

export type RingKey = 'consistency' | 'technical' | 'coding' | 'gd';

export const SCORE_BUCKET_KEYS: ScoreBucketKey[] = ['overall', 'coding', 'technical', 'gd'];

export const SCORE_BUCKET_LABELS: Record<ScoreBucketKey, string> = {
  overall: 'Interview practice',
  coding: 'Coding',
  technical: 'Technical interviews',
  gd: 'Group discussion',
};

export const RING_LABELS: Record<RingKey, string> = {
  consistency: 'Consistency',
  technical: 'Interview',
  coding: 'Coding',
  gd: 'GD',
};

const NOT_PRACTICED_CATEGORY_LABELS: Record<ScoreBucketKey, string> = {
  overall: 'Interview practice',
  coding: 'Coding',
  technical: 'Technical interviews',
  gd: 'Group Discussion',
};

export function getNotPracticedMessage(key: RingKey | ScoreBucketKey): string {
  if (key === 'consistency') return 'Not practiced yet';
  const label = NOT_PRACTICED_CATEGORY_LABELS[key as ScoreBucketKey];
  return label ? `${label} not practiced yet` : 'Not practiced yet';
}

export type RankingTextPart = { type: 'text' | 'percent'; value: string };

export function splitPercentParts(text: string): RankingTextPart[] {
  const parts: RankingTextPart[] = [];
  const re = /(\d+(?:\.\d+)?%)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'percent', value: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
}

export const PEER_COMPARISON_PLACEHOLDER = {
  title: 'Peer comparison unlocks soon',
  body: 'When at least 3 people in your org complete interviews in a category, you’ll see how you compare.',
};

export function shouldHideRankingBoard(data: PeerComparisonResponse | null | undefined): boolean {
  return data?.reason === 'no_org';
}

export function shouldShowPeerComparison(bucket?: ScoreComparison | null): boolean {
  return bucket?.available === true && bucket.better_than_percent != null;
}

export function clampPercent(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, value));
}

export function formatScore(value: number): string {
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
}

function buildAverageScoreMessage(key: RingKey, score: number): string {
  const formatted = formatScore(score);
  switch (key) {
    case 'technical':
      return `Your Interview score on average is ${formatted}%`;
    case 'coding':
      return `Your coding score on average is ${formatted}%`;
    case 'gd':
      return `Your GD score on average is ${formatted}%`;
    default:
      return `Your score on average is ${formatted}%`;
  }
}

export function formatSkillName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatUpdatedAt(iso?: string | null): string | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function hasAnyScoreComparison(data: PeerComparisonResponse): boolean {
  const comparisons = data.score_comparisons;
  if (!comparisons) return false;
  return SCORE_BUCKET_KEYS.some((key) => shouldShowPeerComparison(comparisons[key]));
}

export function getPersonalScoresLine(data: PeerComparisonResponse): string | null {
  const comparisons = data.score_comparisons;
  if (!comparisons) return null;

  const parts: string[] = [];
  const order: { key: ScoreBucketKey; label: string }[] = [
    { key: 'coding', label: 'Coding' },
    { key: 'technical', label: 'Technical' },
    { key: 'overall', label: 'Overall' },
    { key: 'gd', label: 'GD' },
  ];

  for (const { key, label } of order) {
    const bucket = comparisons[key];
    if (bucket?.available && bucket.your_score != null) {
      parts.push(`${label}: ${formatScore(bucket.your_score)}`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export interface RingDisplayValue {
  fillPercent: number | null;
  compactText: string;
  hoverLabel: string;
  hoverValue: string | null;
  showPeerComparison: boolean;
}

export function resolveRingDisplay(
  key: RingKey,
  data?: PeerComparisonResponse | null
): RingDisplayValue {
  if (!data) {
    return {
      fillPercent: null,
      compactText: '—',
      hoverLabel: RING_LABELS[key],
      hoverValue: null,
      showPeerComparison: false,
    };
  }

  if (key === 'consistency') {
    const pct = clampPercent(data.consistency?.better_than_percent);
    const message = data.consistency?.message?.trim();
    const practiceDays = data.consistency?.practice_days_in_window;

    let compactText = '—';
    if (message) {
      compactText = message.length > 72 ? `${message.slice(0, 69)}…` : message;
    } else if (practiceDays != null) {
      compactText = `${practiceDays} of last 30 days practiced`;
    } else if (pct != null) {
      compactText = `${Math.round(pct)}% more consistent`;
    }

    return {
      fillPercent: pct,
      compactText,
      hoverLabel: 'Consistency',
      hoverValue:
        pct != null ? `${Math.round(pct)}%` : practiceDays != null ? `${practiceDays}d` : null,
      showPeerComparison: pct != null || !!message,
    };
  }

  const bucket = data.score_comparisons?.[key];
  if (!bucket || bucket.available === false) {
    return {
      fillPercent: null,
      compactText: getNotPracticedMessage(key),
      hoverLabel: RING_LABELS[key],
      hoverValue: null,
      showPeerComparison: false,
    };
  }

  const pct = clampPercent(bucket.better_than_percent);
  const showPeer = shouldShowPeerComparison(bucket);

  if (showPeer && bucket.message?.trim()) {
    const msg = bucket.message.trim();
    return {
      fillPercent: pct,
      compactText: msg.length > 72 ? `${msg.slice(0, 69)}…` : msg,
      hoverLabel: RING_LABELS[key],
      hoverValue: bucket.your_score != null ? formatScore(bucket.your_score) : pct != null ? `${Math.round(pct)}%` : null,
      showPeerComparison: true,
    };
  }

  if (showPeer && pct != null) {
    return {
      fillPercent: pct,
      compactText: `${Math.round(pct)}% better than peers`,
      hoverLabel: RING_LABELS[key],
      hoverValue: bucket.your_score != null ? formatScore(bucket.your_score) : `${Math.round(pct)}%`,
      showPeerComparison: true,
    };
  }

  if (bucket.your_score != null) {
    return {
      fillPercent: null,
      compactText: buildAverageScoreMessage(key, bucket.your_score),
      hoverLabel: RING_LABELS[key],
      hoverValue: `${formatScore(bucket.your_score)}%`,
      showPeerComparison: false,
    };
  }

  return {
    fillPercent: null,
    compactText: '—',
    hoverLabel: RING_LABELS[key],
    hoverValue: null,
    showPeerComparison: false,
  };
}
